import { useGqtyClient, useTeam } from '@vrplatform/data';
import { showLoadingNotification } from '@vrplatform/theme';
import {
  updateErrorNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { waitFor } from '@vrplatform/utils';
import type { StatementInput } from '@vrplatform/ui-common';
import { useState } from 'react';
import type { LegacySubmitConfirmModalType } from '../../../../components/legacy/legacy-confirm-modal';
import { createZipBlob } from '../../../../utils';
import { getOwnerStatementPdf } from '../pdf';

export const useStatementPdfMultipleExport = (withNotification?: boolean) => {
  const [{ id: teamId }] = useTeam();
  const [loading, setLoading] = useState(false);
  const hasura = useGqtyClient();

  const generatePdf = async (id: string) => {
    const input: StatementInput = {
      groupedBy: 'groupByReservation',
      statementIds: [id],
      ownerId: undefined,
      startAt: '',
      teamId,
      dashboard: 'owner',
    };

    return await getOwnerStatementPdf({
      input,
      hasura,
      opts: {
        autoDownload: false,
      },
    });
  };

  const handleExportPdfs: LegacySubmitConfirmModalType<string[]> = async (
    statementIds
  ) => {
    setLoading(true);
    const notifyId = 'export-statement-pdf';

    if (withNotification) showLoadingNotification({ id: notifyId });

    try {
      await waitFor(100);

      if ((statementIds || []).length < 1) {
        throw Error('Missing selected statements.');
      }

      const files: { filename: string; blob: Blob }[] = [];
      for (const id of statementIds || []) {
        await generatePdf(id).then((file) => {
          if (file) files.push(file);
        });
      }

      await zipBlobs(files, 'pdf');

      if (withNotification) {
        updateSuccessNotification({
          id: notifyId,
          title: 'Statement PDFs',
          message: 'Statements were successfully exported!',
        });
      }

      setLoading(false);
      return {
        message: 'Statements were successfully exported!',
        isSuccess: true,
      };
    } catch (err) {
      console.error(err);

      if (withNotification) {
        updateErrorNotification({
          id: notifyId,
          title: 'Error: Statement PDFs',
          message: 'Failed to export statements. Please try again.',
        });
      }
      setLoading(false);
      return {
        message: (err as any).message || 'Export failed. Please try again.',
        isSuccess: false,
      };
    }
  };

  return {
    handleExportPdfs,
    loading,
  };
};

async function zipBlobs(
  files: { filename: string; blob: Blob }[],
  fileType: 'csv' | 'pdf'
) {
  const zipblob = await createZipBlob(
    files.map((file, index) => {
      const defaultFileName = `statement_${index}`;
      const filename = file.filename;

      return {
        filename: `${filename || defaultFileName}.${fileType}`,
        blob: file.blob,
      };
    })
  );

  const elem = window.document.createElement('a');
  elem.href = window.URL.createObjectURL(zipblob);
  elem.download = 'compressed.zip';
  elem.click();
}
